import React from 'react';
import {
    Button,
    Drawer,
    DrawerHeader,
    DrawerHeaderTitle,
    DrawerBody,
    Dropdown,
    Field,
    Option,
    Spinner,
    Textarea,
} from '@fluentui/react-components';
import { IQuoteRequestAnswer, IQuoteRequestItem } from '../../Api/Models/Cart';
import { useGetQuoteRequestQuestionsQuery, useRequestQuoteMutation } from '../../Api/PreparedCartEndpoints';
import { useLocalization } from '../Helpers/useLocalization';

interface IQuoteRequestDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    // Lines the quote covers. The cart passes its whole contents; a product page passes the one item.
    items: IQuoteRequestItem[];
    // Optional line shown above the form (e.g. the product name) so a single-item request has context.
    subject?: string;
}

/**
 * The Request-a-Quote panel. Originally lived inside CartSummary; extracted so the quote-only price
 * placeholder on the product pages can open the same flow for a single item.
 * Callers are responsible for gating on `allowRequestQuote` / `defaultSalesRep`.
 */
const QuoteRequestDrawer: React.FC<IQuoteRequestDrawerProps> = (props) => {
    const L = useLocalization();
    const [comment, setComment] = React.useState('');
    const [error, setError] = React.useState<string | undefined>();
    const [success, setSuccess] = React.useState(false);
    const [answers, setAnswers] = React.useState<string[]>([]);
    const [requestQuote, { isLoading: submitting }] = useRequestQuoteMutation();
    const quoteRequestQuestions = useGetQuoteRequestQuestionsQuery(undefined, { skip: !props.open });

    const questions = React.useMemo(
        () => quoteRequestQuestions.data?.questions ?? [],
        [quoteRequestQuestions.data?.questions]
    );

    React.useEffect(() => {
        if (questions.length === 0) return;
        setAnswers((prev) => (prev.length === questions.length ? prev : new Array(questions.length).fill('')));
    }, [questions]);

    // Every open starts a fresh request — otherwise the previous success message / comment sticks around.
    React.useEffect(() => {
        if (!props.open) return;
        setSuccess(false);
        setError(undefined);
        setComment('');
        setAnswers(new Array(questions.length).fill(''));
        // Intentionally keyed on `open` only: re-running when the questions load would wipe typed answers.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.open]);

    const allRequiredAnswered = questions.every((q, i) => !q.isRequired || (answers[i] ?? '').trim() !== '');

    const submit = async () => {
        setError(undefined);
        const builtAnswers: IQuoteRequestAnswer[] = questions.map((q, i) => ({
            identifier: q.identifier ?? '',
            questionText: q.questionText ?? '',
            answer: answers[i] ?? '',
        })).filter(a => a.answer.trim() !== '');
        const result = await requestQuote({
            request: { items: props.items, comment, questions: builtAnswers.length > 0 ? builtAnswers : undefined },
        });
        if ('error' in result) {
            setError(L('localization_cart_quote_error'));
            return;
        }
        if (!result.data?.success) {
            setError(result.data?.error ?? result.data?.message ?? L('localization_cart_quote_error'));
            return;
        }
        setSuccess(true);
    };

    return (
        <Drawer
            position='end'
            size={'medium'}
            open={props.open}
            onOpenChange={(_, { open }) => props.onOpenChange(open)}
            // The drawer is portaled to the body, but React synthetic events bubble through the COMPONENT
            // tree, not the DOM tree. Opened from a product card or a search row — both of which navigate
            // on click — every click in here reached that ancestor handler, so interacting with the panel
            // sent the shopper to the product page and unmounted the panel mid-use. Stopping here rather
            // than at each call site: a modal surface's clicks should not be an ancestor's business, and
            // Fluent's own handlers sit inside this subtree so they have already run by now.
            onClick={e => e.stopPropagation()}
        >
            <DrawerHeader>
                <DrawerHeaderTitle>{L('localization_cart_request_quote')}</DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody style={{ display: 'flex', flexDirection: 'column', gap: '1em', padding: '1em' }}>
                {success ? (
                    <>
                        <p className="quote-success-msg">{L('localization_cart_quote_success')}</p>
                        <Button onClick={() => props.onOpenChange(false)}>{L('localization_common_close')}</Button>
                    </>
                ) : (
                    <>
                        {props.subject && <p className='quote-subject' style={{ fontWeight: 600, margin: 0 }}>{props.subject}</p>}
                        {quoteRequestQuestions.isLoading && <Spinner label={L('localization_cart_dialog_loading')} size='tiny' />}
                        {questions.map((q, i) => {
                            const handleChange = (value: string) => {
                                const next = [...answers];
                                next[i] = value;
                                setAnswers(next);
                            };
                            if (q.answers && q.answers.length > 0) {
                                return (
                                    <Field key={q.identifier ?? i} label={q.questionText} required={q.isRequired}>
                                        <Dropdown
                                            value={answers[i] || ''}
                                            onOptionSelect={(_, data) => handleChange(data.optionValue ?? '')}
                                            style={{ width: '100%' }}
                                        >
                                            {q.answers.map((ans, j) => (
                                                <Option key={j} value={ans}>{ans}</Option>
                                            ))}
                                        </Dropdown>
                                    </Field>
                                );
                            }
                            return (
                                <Field key={q.identifier ?? i} label={q.questionText} required={q.isRequired}>
                                    <Textarea
                                        value={answers[i] || ''}
                                        onChange={e => handleChange(e.target.value)}
                                        style={{ width: '100%' }}
                                    />
                                </Field>
                            );
                        })}
                        <Field label={L('localization_cart_quote_comment_label')}>
                            <Textarea
                                value={comment}
                                onChange={(_, d) => setComment(d.value)}
                                placeholder={L('localization_cart_quote_comment_placeholder')}
                                rows={5}
                            />
                        </Field>
                        {error && <p style={{ color: 'red' }}>{error}</p>}
                        <Button
                            appearance='primary'
                            onClick={submit}
                            disabled={submitting || !allRequiredAnswered || props.items.length === 0}
                            icon={submitting ? <Spinner size='tiny' /> : undefined}
                        >
                            {submitting ? L('localization_cart_quote_submitting') : L('localization_cart_quote_submit')}
                        </Button>
                        <Button appearance='secondary' onClick={() => props.onOpenChange(false)}>{L('localization_common_cancel')}</Button>
                    </>
                )}
            </DrawerBody>
        </Drawer>
    );
};

export default QuoteRequestDrawer;
